import { useEffect } from 'react'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest } from './useSwrRequest'

export type FollowStatus = {
  followable: boolean
  following: boolean
}

/**
 * Fetches the follow status ahead of `@fs/zion-follow` so the banner can hold a placeholder until it is known,
 * instead of Follow rendering nothing during its own fetch. Mirrors zion-follow's toggle events into the SWR cache
 * so a remounted Follow gets a current `initialState`.
 */
export default function useFollowStatus(
  personId: string | undefined
): SwrResponse<FollowStatus> & { followStatus: FollowStatus | undefined } {
  const response = useSwrRequest<FollowStatus>(
    personId ? `/service/tree/tree-data/watch/${personId}/status` : null,
    undefined,
    {
      revalidateOnFocus: false,
      silentServerErrors: true,
    }
  )
  const { mutate } = response

  useEffect(() => {
    if (!personId) {
      return undefined
    }
    const handleFollowed = (): void => {
      mutate({ followable: true, following: true }, false)
    }
    const handleUnfollowed = (): void => {
      mutate({ followable: true, following: false }, false)
    }
    document.body.addEventListener(`followed-${personId}`, handleFollowed)
    document.body.addEventListener(`unfollowed-${personId}`, handleUnfollowed)
    return () => {
      document.body.removeEventListener(`followed-${personId}`, handleFollowed)
      document.body.removeEventListener(`unfollowed-${personId}`, handleUnfollowed)
    }
  }, [mutate, personId])

  return { ...response, followStatus: response.data }
}
